import React, { useState, useEffect, useCallback } from "react";
import { Navigate } from "react-router-dom";
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Modal,
  Form,
  InputNumber,
  Row,
  Col,
  Typography,
  Tooltip,
  Popconfirm,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import {
  getAllRawMaterials,
  createRawMaterial,
  updateRawMaterial,
  deleteRawMaterial,
  getLowStockMaterials,
} from "../../api/inventoryApi";
import PageTitle from "../page-header/PageHeader";
import checkTokenExp from "../../utils/checkTokenExp";

const { Text } = Typography;
const { Search } = Input;
const { Option } = Select;

// ── Categories matching backend enum ─────────────────────────
const MATERIAL_CATEGORIES = [
  { value: "PAPER", label: "Paper / Board" },
  { value: "GUM_CORR", label: "Gum (Corrugation)" },
  { value: "GUM_PASTE", label: "Gum (Pasting)" },
  { value: "INK", label: "Ink" },
  { value: "WIRE", label: "Stitching Wire" },
  { value: "SUTLI", label: "Sutli" },
  { value: "FILM", label: "Film" },
  { value: "STRAP", label: "Strapping Roll" },
];

const PAPER_TYPES = [
  "Kraft", "Semi Kraft", "Agro", "RG", "Duplex",
  "SBS", "Korean Kraft", "Star", "Fluting Medium",
];

const UNITS = [
  { value: "kg", label: "Kg" },
  { value: "liters", label: "Litres" },
  { value: "meters", label: "Metres" },
  { value: "pieces", label: "Pieces" },
];

const RawMaterialList = () => {
  // ── Auth ───────────────────────────────────────────────────
  const isLogged = Boolean(localStorage.getItem("isLogged"));
  const accessToken = localStorage.getItem("access-token");

  // ── State ──────────────────────────────────────────────────
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 50, total: 0 });
  const [searchText, setSearchText] = useState("");
  const [filterCategory, setFilterCategory] = useState(undefined);

  // Modal
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  // Low stock alert
  const [lowStockCount, setLowStockCount] = useState(0);

  // Watch category in form for conditional fields
  const formCategory = Form.useWatch("category", form);
  const formPaperType = Form.useWatch("paper_type", form);
  const formGsm = Form.useWatch("gsm", form);
  const formBf = Form.useWatch("bf", form);
  const formGumType = Form.useWatch("gum_type", form);
  const formViscosity = Form.useWatch("viscosity", form);
  const formInkColor = Form.useWatch("ink_color", form);
  const formInkType = Form.useWatch("ink_type", form);
  const formGauge = Form.useWatch("gauge", form);

  // ── Auto-generate material name from selections ────────────
  const generateMaterialName = useCallback(() => {
    const cat = form.getFieldValue("category");
    if (!cat) return "";

    const parts = [];

    switch (cat) {
      case "PAPER": {
        const pt = form.getFieldValue("paper_type");
        const gsm = form.getFieldValue("gsm");
        const bf = form.getFieldValue("bf");
        // Width is now tracked at stock/batch level, not in material name
        if (pt) parts.push(pt.toUpperCase().replace(/\s+/g, "-"));
        if (gsm) parts.push(`${gsm}`);
        if (bf) parts.push(`${bf}BF`);
        break;
      }
      case "GUM_CORR": {
        parts.push("CORR GUM");
        const gt = form.getFieldValue("gum_type");
        const v = form.getFieldValue("viscosity");
        if (gt) parts.push(gt.toUpperCase());
        if (v) parts.push(v.toUpperCase());
        break;
      }
      case "GUM_PASTE": {
        parts.push("PASTE GUM");
        const gt = form.getFieldValue("gum_type");
        const v = form.getFieldValue("viscosity");
        if (gt) parts.push(gt.toUpperCase());
        if (v) parts.push(v.toUpperCase());
        break;
      }
      case "INK": {
        parts.push("INK");
        const c = form.getFieldValue("ink_color");
        const t = form.getFieldValue("ink_type");
        if (c) parts.push(c.toUpperCase());
        if (t) parts.push(t.toUpperCase());
        break;
      }
      case "WIRE": {
        parts.push("WIRE");
        const g = form.getFieldValue("gauge");
        if (g) parts.push(`${g}G`);
        break;
      }
      case "SUTLI":
        parts.push("SUTLI");
        break;
      case "PLATE":
        parts.push("PRINTING PLATE");
        break;
      case "DIE":
        parts.push("DIE");
        break;
      case "FILM":
        parts.push("FILM");
        break;
      case "STRAP":
        parts.push("STRAPPING ROLL");
        break;
      default:
        parts.push(cat);
    }

    return parts.join("-").replace(/\s+/g, "-");
  }, [form]);

  // Auto-set name whenever relevant fields change
  useEffect(() => {
    if (modalVisible) {
      const name = generateMaterialName();
      if (name) form.setFieldValue("name", name);
    }
  }, [
    formCategory, formPaperType, formGsm, formBf,
    formGumType, formViscosity, formInkColor, formInkType, formGauge,
    modalVisible, generateMaterialName, form,
  ]);

  // ── Fetch ──────────────────────────────────────────────────
  const fetchMaterials = useCallback(async (page = 1, limit = 50) => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (searchText) params.search = searchText;
      if (filterCategory) params.category = filterCategory;
      const result = await getAllRawMaterials(params);
      setData(result?.data || []);
      const pg = result?.pagination || {};
      setPagination({
        current: pg.page || page,
        pageSize: pg.limit || limit,
        total: pg.total || 0,
      });
    } catch (error) {
      console.error(error.message);
      toast.error("Failed to load raw materials");
    } finally {
      setLoading(false);
    }
  }, [searchText, filterCategory]);

  const fetchLowStockCount = useCallback(async () => {
    try {
      const result = await getLowStockMaterials();
      setLowStockCount(Array.isArray(result) ? result.length : 0);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    fetchMaterials();
  }, [fetchMaterials]);

  useEffect(() => {
    fetchLowStockCount();
  }, [fetchLowStockCount]);

  // ── Auth guard (after hooks) ───────────────────────────────
  if (!isLogged) {
    return <Navigate to={"/auth/login"} replace={true} />;
  }
  checkTokenExp(accessToken);

  // ── Handlers ───────────────────────────────────────────────
  const handleTableChange = (pag) => {
    fetchMaterials(pag.current, pag.pageSize);
  };

  const openCreateModal = () => {
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({ category: "PAPER", unit: "kg", lead_time_days: 7, name: "" });
    setModalVisible(true);
  };

  const openEditModal = (record) => {
    setEditingRecord(record);
    const cfg = record.config || {};
    form.setFieldsValue({
      name: record.name,
      category: record.category || "PAPER",
      hsn_code: record.hsn_code,
      unit: record.unit || "kg",
      paper_type: cfg.paper_type,
      gsm: cfg.gsm,
      bf: cfg.bf,
      gum_type: cfg.gum_type,
      viscosity: cfg.viscosity,
      ink_color: cfg.color,
      ink_type: cfg.type,
      gauge: cfg.gauge,
      reorder_level: record.reorder_level,
      reorder_quantity: record.reorder_quantity,
      safety_stock: record.safety_stock,
      lead_time_days: record.lead_time_days,
      standard_cost: record.standard_cost,
      moq_quantity: record.moq_quantity,
      moq_unit: record.moq_unit,
    });
    setModalVisible(true);
  };

  const buildPayload = (values) => {
    const payload = {
      name: values.name,
      category: values.category,
      hsn_code: values.hsn_code || undefined,
      unit: values.unit,
      reorder_level: values.reorder_level || 0,
      reorder_quantity: values.reorder_quantity || 0,
      safety_stock: values.safety_stock || 0,
      lead_time_days: values.lead_time_days || 7,
      standard_cost: values.standard_cost || undefined,
      // MOQ settings
      moq_quantity: values.moq_quantity || undefined,
      moq_unit: values.moq_unit || undefined,
      moq_weight_formula: values.category === "PAPER" ? "width * 14" : undefined,
    };
    if (values.category === "PAPER") {
      payload.config = { paper_type: values.paper_type, gsm: values.gsm, bf: values.bf };
      // Default MOQ for paper is 1 reel if not set
      if (!payload.moq_unit) payload.moq_unit = "reel";
      if (!payload.moq_quantity) payload.moq_quantity = 1;
    } else if (values.category === "GUM_CORR" || values.category === "GUM_PASTE") {
      payload.config = { gum_type: values.gum_type, viscosity: values.viscosity };
    } else if (values.category === "INK") {
      payload.config = { color: values.ink_color, type: values.ink_type };
    } else if (values.category === "WIRE") {
      payload.config = { gauge: values.gauge, type: "stitching" };
    }
    return payload;
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = buildPayload(values);

      if (editingRecord) {
        await updateRawMaterial(editingRecord.id, payload);
        toast.success("Raw material updated");
      } else {
        await createRawMaterial(payload);
        toast.success("Raw material created");
      }
      setModalVisible(false);
      fetchMaterials(pagination.current, pagination.pageSize);
    } catch (error) {
      if (error?.errorFields) return;
      console.error(error.message);
      toast.error(editingRecord ? "Failed to update" : "Failed to create");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record) => {
    try {
      await deleteRawMaterial(record.id);
      toast.success("Raw material deactivated");
      fetchMaterials(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error(error.message);
      toast.error("Failed to delete raw material");
    }
  };

  const paperSpec = (record) => {
    const c = record.config || {};
    const parts = [];
    if (c.paper_type) parts.push(c.paper_type);
    if (c.gsm) parts.push(`${c.gsm} GSM`);
    if (c.bf) parts.push(`BF ${c.bf}`);
    // Width is now at stock/batch level, not material master
    return parts.join(" / ") || "—";
  };

  // ── Columns ────────────────────────────────────────────────
  const columns = [
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      width: 140,
      render: (val) => <Text strong>{val || "—"}</Text>,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      ellipsis: true,
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: 140,
      render: (val) => {
        const cat = MATERIAL_CATEGORIES.find((c) => c.value === val);
        return <Tag>{cat?.label || val || "—"}</Tag>;
      },
    },
    {
      title: "Spec / Config",
      key: "config",
      width: 200,
      ellipsis: true,
      render: (_, record) => {
        if (record.category === "PAPER") return paperSpec(record);
        const c = record.config || {};
        return Object.values(c).filter(Boolean).join(", ") || "—";
      },
    },
    {
      title: "Unit",
      dataIndex: "unit",
      key: "unit",
      width: 70,
    },
    {
      title: "Reorder Lvl",
      dataIndex: "reorder_level",
      key: "reorder_level",
      width: 100,
      render: (val) => (val ? val : "—"),
    },
    {
      title: "Safety Stock",
      dataIndex: "safety_stock",
      key: "safety_stock",
      width: 100,
      render: (val) => (val ? val : "—"),
    },
    {
      title: "Std Cost",
      dataIndex: "standard_cost",
      key: "standard_cost",
      width: 100,
      render: (val) => (val != null ? `₹${Number(val).toFixed(2)}` : "—"),
    },
    {
      title: "Last Price",
      dataIndex: "last_purchase_price",
      key: "last_purchase_price",
      width: 100,
      render: (val) => (val != null ? `₹${Number(val).toFixed(2)}` : "—"),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      width: 80,
      render: (val) => (
        <Tag color={val !== false ? "success" : "default"}>
          {val !== false ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      fixed: "right",
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEditModal(record)} />
          </Tooltip>
          <Popconfirm title="Deactivate this material?" onConfirm={() => handleDelete(record)} okText="Yes" cancelText="No">
            <Tooltip title="Deactivate">
              <Button type="text" size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ── Render ─────────────────────────────────────────────────
  return (
    <div>
      <PageTitle title="Back" subtitle="Raw Materials (Consumables)" />

      {lowStockCount > 0 && (
        <Card size="small" style={{ marginBottom: 16, borderColor: "#faad14" }}>
          <Space>
            <WarningOutlined style={{ color: "#faad14", fontSize: 18 }} />
            <Text type="warning" strong>
              {lowStockCount} material{lowStockCount > 1 ? "s" : ""} below reorder level
            </Text>
          </Space>
        </Card>
      )}

      <Card
        title={
          <Space>
            <span>Raw Materials (Consumables)</span>
            <Tag>{pagination.total} total</Tag>
          </Space>
        }
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
            Add Material
          </Button>
        }
      >
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={10} md={8}>
            <Search
              placeholder="Search by name or code..."
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={() => fetchMaterials(1)}
              enterButton={<SearchOutlined />}
            />
          </Col>
          <Col xs={12} sm={8} md={6}>
            <Select
              placeholder="Category"
              allowClear
              style={{ width: "100%" }}
              value={filterCategory}
              onChange={(val) => setFilterCategory(val)}
              options={MATERIAL_CATEGORIES}
            />
          </Col>
          <Col>
            <Tooltip title="Refresh">
              <Button icon={<ReloadOutlined />} onClick={() => fetchMaterials(1)} />
            </Tooltip>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={data}
          rowKey={(r) => r.id || r.code}
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: [20, 50, 100],
            showTotal: (total) => `Total ${total} materials`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1300 }}
          size="small"
        />
      </Card>

      {/* ── Create / Edit Modal ────────────────────────────── */}
      <Modal
        title={editingRecord ? `Edit — ${editingRecord.code || ""}` : "Add Raw Material"}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        confirmLoading={saving}
        width={720}
        okText={editingRecord ? "Update" : "Create"}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="category" label="Category" rules={[{ required: true }]}>
                <Select
                  options={MATERIAL_CATEGORIES}
                  onChange={() => {
                    // Clear all config fields when category changes
                    form.setFieldsValue({
                      paper_type: undefined, gsm: undefined, bf: undefined, width: undefined,
                      gum_type: undefined, viscosity: undefined,
                      ink_color: undefined, ink_type: undefined,
                      gauge: undefined,
                    });
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="unit" label="Unit" rules={[{ required: true }]}>
                <Select options={UNITS} />
              </Form.Item>
            </Col>
          </Row>

          {/* Paper config */}
          {formCategory === "PAPER" && (
            <>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="paper_type" label="Paper Type">
                    <Select allowClear placeholder="Select">
                      {PAPER_TYPES.map((t) => (<Option key={t} value={t}>{t}</Option>))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="gsm" label="GSM">
                    <InputNumber min={0} max={999} style={{ width: "100%" }} placeholder="180" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="bf" label="BF">
                    <InputNumber min={0} max={99} style={{ width: "100%" }} placeholder="22" />
                  </Form.Item>
                </Col>
              </Row>
              <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
                💡 Reel width is captured when adding stock (each reel can have different width)
              </Text>
            </>
          )}

          {/* Gum config */}
          {(formCategory === "GUM_CORR" || formCategory === "GUM_PASTE") && (
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="gum_type" label="Gum Type">
                  <Input placeholder="e.g. Corrugation, Pasting" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="viscosity" label="Viscosity">
                  <Select allowClear placeholder="Select">
                    <Option value="low">Low</Option>
                    <Option value="medium">Medium</Option>
                    <Option value="high">High</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          )}

          {/* Ink config */}
          {formCategory === "INK" && (
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="ink_color" label="Color">
                  <Input placeholder="e.g. Cyan, Black" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="ink_type" label="Ink Type">
                  <Select allowClear placeholder="Select">
                    <Option value="offset">Offset</Option>
                    <Option value="flexo">Flexo</Option>
                    <Option value="digital">Digital</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          )}

          {/* Wire config */}
          {formCategory === "WIRE" && (
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="gauge" label="Gauge">
                <Input style={{ width: "100%" }} placeholder="e.g. 23, 50, 18A" />
                </Form.Item>
              </Col>
            </Row>
          )}

          {/* Auto-generated name (read-only) */}
          <Row gutter={16}>
            <Col span={14}>
              <Form.Item name="name" label="Material Name (Auto-Generated)" rules={[{ required: true, message: "Select category and config fields above" }]}
                tooltip="Auto-generated from selections above"
              >
                <Input readOnly style={{ fontWeight: 600, backgroundColor: "#f5f5f5", fontSize: 15 }} placeholder="Select options above..." />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item name="hsn_code" label="HSN Code">
                <Input placeholder="e.g. 4805" />
              </Form.Item>
            </Col>
          </Row>

          {/* Inventory settings */}
          <Text strong style={{ display: "block", marginBottom: 12, marginTop: 8 }}>
            Inventory Settings
          </Text>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="reorder_level" label="Reorder Level">
                <InputNumber min={0} style={{ width: "100%" }} placeholder="500" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="reorder_quantity" label="Reorder Qty">
                <InputNumber min={0} style={{ width: "100%" }} placeholder="2000" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="safety_stock" label="Safety Stock">
                <InputNumber min={0} style={{ width: "100%" }} placeholder="200" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="lead_time_days" label="Lead Time (days)">
                <InputNumber min={0} style={{ width: "100%" }} placeholder="7" />
              </Form.Item>
            </Col>
          </Row>

          {/* Minimum Order Quantity (MOQ) */}
          <Text strong style={{ display: "block", marginBottom: 12, marginTop: 8 }}>
            Minimum Order Quantity (MOQ)
          </Text>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="moq_quantity" label="MOQ Quantity">
                <InputNumber min={0} style={{ width: "100%" }} placeholder={formCategory === "PAPER" ? "1" : "100"} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="moq_unit" label="MOQ Unit">
                <Select placeholder="Select" allowClear>
                  <Option value="reel">Reel</Option>
                  <Option value="kg">Kg</Option>
                  <Option value="liters">Litres</Option>
                  <Option value="pieces">Pieces</Option>
                  <Option value="box">Box</Option>
                  <Option value="roll">Roll</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              {formCategory === "PAPER" && (
                <Form.Item label="Reel Weight Formula" tooltip="Empirical: Reel Weight (kg) = Width × 14 (±5%)">
                  <Input value="width × 14" disabled style={{ backgroundColor: "#f5f5f5" }} />
                </Form.Item>
              )}
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="standard_cost" label="Standard Cost (₹/unit)">
                <InputNumber min={0} step={0.01} style={{ width: "100%" }} placeholder="45.00" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default RawMaterialList;
